import type {
  Conversation,
  ConversationType,
  InboxFolder,
  InboxSelection,
  PlatformFilter,
} from './types';

/**
 * Pure filter/count helpers for the consolidated social inbox. Conversations
 * now come from the API (GET /messages/conversations); these operate on whatever
 * list is passed in.
 */

/** Does a conversation belong in the given left-rail folder? */
function inFolder(conversation: Conversation, folder: InboxFolder): boolean {
  switch (folder) {
    case 'inbox':
      return true;
    case 'unread':
      return conversation.unread;
    case 'mentions':
      return conversation.type === 'mention';
    case 'comments':
      return conversation.type === 'comment';
    case 'dms':
      return conversation.type === 'dm';
    default:
      return true;
  }
}

/** Count of conversations in each folder. */
export function folderCounts(conversations: Conversation[]): Record<InboxFolder, number> {
  const folders: InboxFolder[] = ['inbox', 'unread', 'mentions', 'comments', 'dms'];
  return folders.reduce(
    (acc, folder) => {
      acc[folder] = conversations.filter((c) => inFolder(c, folder)).length;
      return acc;
    },
    {} as Record<InboxFolder, number>,
  );
}

/** Conversation count per platform (plus 'all'). */
export function platformCounts(conversations: Conversation[]): Record<PlatformFilter, number> {
  const counts = { all: conversations.length } as Record<PlatformFilter, number>;
  for (const c of conversations) {
    counts[c.platform] = (counts[c.platform] ?? 0) + 1;
  }
  return counts;
}

/** Conversation count per connected account (by connectionId), for the per-account rail rows. */
export function accountCounts(conversations: Conversation[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const c of conversations) {
    if (c.connectionId) counts[c.connectionId] = (counts[c.connectionId] ?? 0) + 1;
  }
  return counts;
}

/** Apply the active folder + rail selection (all / one platform / one account) to a list. */
export function filterConversations(
  conversations: Conversation[],
  folder: InboxFolder,
  selection: InboxSelection,
): Conversation[] {
  return conversations.filter((c) => {
    if (!inFolder(c, folder)) return false;
    switch (selection.kind) {
      case 'all':
        return true;
      case 'platform':
        return c.platform === selection.platform;
      case 'account':
        return c.connectionId === selection.connectionId;
    }
  });
}

/** Short label for a conversation type, used by the list-row badge. */
export const CONVERSATION_TYPE_LABEL: Record<ConversationType, string> = {
  dm: 'DM',
  comment: 'Comment',
  mention: 'Mention',
};
